import * as React from 'react';
import {
  alertClasses as muiAlertClasses,
  capitalize,
  useThemeProps,
} from '@mui/material';
import {
  ComponentsProps,
  ComponentsVariants,
  styled,
} from '@mui/material/styles';
import { getToken } from '@workiva/unify-tokens';
import {
  AlertBlockedPropKeys,
  type UnsupportedProps as AlertUnsupportedProps,
} from 'src/components/Alert/Alert';
import {
  defaultAlertBaseContextValue,
  IAlertBaseContext,
} from 'src/components/AlertBase/AlertBaseContext';
import {
  AlertBase,
  alertBaseHorizontalPaddingInThemeSpacing,
  alertBaseVerticalPaddingInThemeSpacing,
  type AlertBaseProps,
} from 'src/components/AlertBase/AlertBase';
import { getAlertBaseStyledOverridesResolver } from 'src/components/AlertBase/styledAlertBaseOverridesResolver';
import { getAlertBaseUtilityClass } from 'src/components/AlertBase/alertBaseClasses';
import { toastClasses, type ToastClassKey } from './toastClasses';
import clsx from 'clsx';

// Module augmentation to add this component to the theme for component overrides / default props
declare module '@mui/material/styles' {
  interface ComponentNameToClassKey {
    UnifyToast: ToastClassKey;
  }

  interface ComponentsPropsList {
    UnifyToast: ToastProps;
  }

  interface Components {
    UnifyToast?: {
      defaultProps?: ComponentsProps['UnifyToast'];
      variants?: ComponentsVariants['UnifyToast'];
    };
  }
}

export type ToastProps = Omit<
  AlertBaseProps,
  | keyof AlertUnsupportedProps
  | 'showIcon'
  | 'components'
  | 'componentsProps'
  | AlertBlockedPropKeys
>;

const UnifyToastRoot = styled(AlertBase, {
  name: 'UnifyToast',
  slot: 'Root',
  overridesResolver: getAlertBaseStyledOverridesResolver('root'),
})<
  Omit<ToastProps, 'severity'> & Required<Pick<IAlertBaseContext, 'severity'>>
>(({ action, actionPlacement, onClose, severity, theme }) => {
  const severityToken = severity === 'info' ? 'primary' : severity;
  const iconWidthInPx = 56;
  const rootPaddingInPx =
    theme.spacingScalingFactor * alertBaseHorizontalPaddingInThemeSpacing;
  // Have to increase the left padding since the icon is absolutely positioned
  const computedLeftPaddingInThemeSpacing =
    (iconWidthInPx + rootPaddingInPx) / theme.spacingScalingFactor;
  // In order for Toast to have the same intrinsic height as Alert, we need
  // to add 1px extra padding to the top/bottom since Toast has no border
  // to contribute those 2px to the box-model height.
  const computedVerticalPaddingInPx =
    alertBaseVerticalPaddingInThemeSpacing * theme.spacingScalingFactor + 1;

  return {
    position: 'relative', // So that we can use absolute positioning for the icon.
    paddingLeft: theme.spacing(computedLeftPaddingInThemeSpacing),
    paddingTop: computedVerticalPaddingInPx,
    paddingBottom: computedVerticalPaddingInPx,
    minWidth: '320px',
    maxWidth: '460px',

    // Because the Toast icon is displayed using absolute positioning,
    // Override the `flexBasis: 0` style that is added via AlertBaseRoot styles.
    [`.${muiAlertClasses.message}`]: {
      flexBasis: 'auto',
      // And then re-instate the `flexBasis: 0` styling when actionPlacement=footer
      // and there is both a close button and one or more actions wrapped by a <Stack>
      // element that gets `display: contents` styling from AlertBase.
      ...(actionPlacement === 'footer' &&
        onClose &&
        action && {
          flexBasis: 0,
        }),
    },

    [`.${muiAlertClasses.icon}`]: {
      position: 'absolute',
      width: iconWidthInPx,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      top: 0,
      bottom: 0,
      left: 0,
      borderTopLeftRadius: (theme.vars ?? theme).shape.borderRadius,
      borderBottomLeftRadius: (theme.vars ?? theme).shape.borderRadius,
    },

    [`&.${getAlertBaseUtilityClass(`severity${capitalize(severity)}`)}`]: {
      color: getToken(`Core/Text/text-default`),
      backgroundColor: getToken('Component/Toast/toast-default-background'),

      [`.${muiAlertClasses.icon}`]: {
        color:
          severity === 'warning'
            ? getToken('Core/Icon/icon-onWarning')
            : getToken('Core/Icon/icon-onColor'),
        backgroundColor: getToken(
          `Component/Toast/toast-${severityToken}-background`
        ),
      },
    },
  };
});

/**
 * DO NOT use this component directly. Use the notification service to render Toasts instead. See docs: https://frontend.workiva.dev/topics/shell/unify-toasts.html Source: https://github.com/Workiva/frontend_docs/blob/master/pages/topics/shell/unify-toasts.md
 *
 * Toasts display brief notifications that provide feedback or updates to the user.
 */
/**
 * A Toast is a lightweight, non-disruptive notification that delivers brief messages to users.
 *
 * Demos:
 *
 * - [Toast](https://unify.workiva.org/components/toast?tab=code)
 */
export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  function Toast(inProps, forwardedRef) {
    const props = useThemeProps({
      props: inProps,
      name: 'UnifyToast',
    });

    const { children, className, severity: severityIn, ...rest } = props;

    const rootClasses = clsx([toastClasses.root, className]);

    const severity = severityIn ?? defaultAlertBaseContextValue.severity;

    return (
      <UnifyToastRoot
        ref={forwardedRef}
        className={rootClasses}
        elevation={4}
        severity={severity}
        {...rest}
      >
        {children}
      </UnifyToastRoot>
    );
  }
);

export default Toast;
